import { atom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';
import type { MapRef } from 'react-map-gl';

import { createMapSourceAtom, getMapDefaultConfig } from 'components/map';

import type { MapQueryCamera } from './types';

const defaults = getMapDefaultConfig();

/**
 * The Map Query map's layer settings, apart from the wizard's other map so the
 * two keep their own. The tracts are the subject here, so the wells and
 * development areas the shared map shows by default start off, and the scroll
 * wheel zooms, as the visible-extent filter needs. The development-area layer
 * draws from its options, not `checked`, so they are all off too, as Map Query's
 * map has them (pages/map-query/map-section/map-section-config.ts).
 *
 * Bump the `-v2` storage key if these defaults change in a way that existing
 * users shouldn't inherit (v1 left the units on).
 */
export const mapQueryMapConfigAtom = atomWithStorage(
  'insights-package-map-query-map-config-v2',
  getMapDefaultConfig({
    wellsV2: { ...defaults.wellsV2, checked: false },
    developmentAreas: {
      checked: false,
      options: {
        horizontalUnits: false,
        verticalUnits: false,
        fieldUnits: false,
        pricing: false,
      },
    },
    scrollZoom: { checked: true },
    popups: { checked: false },
  })
);

/**
 * The Map Query map's handle, one per mounted map: create it in a `useMemo`.
 *
 * @return {PrimitiveAtom} An atom holding the map, null before it mounts
 */
export const createMapRefAtom = () => atom<MapRef | null>(null);

/**
 * Where the Map Query map was last left in this wizard, null before it moved:
 * the map reopens there when the step comes back.
 */
export const mapQueryCameraAtom = atom<MapQueryCamera | null>(null);

/**
 * The filters (`resultsKey`) the Map Query map last fitted to their results, so
 * the map reopened with them unchanged keeps its camera.
 */
export const mapQueryFittedKeyAtom = atom<string | null>(null);

/**
 * The tracts map source's TileJSON address, from the one /maps/sources read the
 * app's map layers share; undefined until it has loaded.
 */
export const tractsSourceHrefAtom = createMapSourceAtom('tracts');
